import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import logoIcon from "@/assets/logo-icon.webp";

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="py-16 md:py-24 bg-warm-black border-t border-primary-foreground/5">
      <div className="container-luxury">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 mb-16">
          {/* Brand Column */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-display text-primary-foreground/80 text-lg tracking-wider mb-4">
              ARZANI GROUP
            </h3>
            <p className="font-display text-primary-foreground/80 text-lg tracking-wider mb-8">
              WORLDWIDE
            </p>
            <img src={logoIcon} alt="Arzani Group" className="h-16 opacity-60" />
          </div>

          {/* Destinations Column */}
          <div>
            <h4 className="font-display text-primary-foreground/80 text-sm tracking-[0.2em] mb-6">
              {t("DESTINATIONS", "BESTEMMINGEN")}
            </h4>
             <ul className="space-y-3">
              {[
                { name: "Ibiza", path: "/ibiza" },
                { name: "Marbella", path: "/marbella" },
                { name: "Dubai", path: "/dubai" },
                { name: "Saint-Tropez", path: "/saint-tropez" },
                { name: t("Paris", "Parijs"), path: "/paris" },
              ].map((dest) => (
                <li key={dest.name}>
                  {dest.path ? (
                    <Link to={dest.path} className="font-label text-primary-foreground/30 hover:text-primary-foreground/70 transition-colors duration-700 text-xs tracking-wider uppercase">
                      {dest.name}
                    </Link>
                  ) : (
                    <a href="#destinations" className="font-label text-primary-foreground/30 hover:text-primary-foreground/70 transition-colors duration-700 text-xs tracking-wider uppercase">
                      {dest.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Arzani Group Column */}
          <div>
            <h4 className="font-display text-primary-foreground/80 text-sm tracking-[0.2em] mb-6">
              ARZANI GROUP
            </h4>
            <ul className="space-y-3">
              {[
                { label: t("About Us", "Over Ons"), href: "/about" },
                { label: t("Our Mission", "Onze Missie"), href: "/about#mission" },
                { label: t("Careers", "Vacatures"), href: "#" },
                { label: "Contact", href: "/contact" },
              ].map((item) => (
                <li key={item.label}>
                    {item.href.startsWith("/") ? (
                      <Link to={item.href} className="font-label text-primary-foreground/30 hover:text-primary-foreground/70 transition-colors duration-700 text-xs tracking-wider uppercase">
                        {item.label}
                      </Link>
                    ) : (
                      <a href={item.href} className="font-label text-primary-foreground/30 hover:text-primary-foreground/70 transition-colors duration-700 text-xs tracking-wider uppercase">
                        {item.label}
                      </a>
                    )}
                </li>
              ))}
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h4 className="font-display text-primary-foreground/80 text-sm tracking-[0.2em] mb-6">
              {t("SUPPORT", "ONDERSTEUNING")}
            </h4>
            <ul className="space-y-3 mb-8">
              {[
                { label: t("Terms", "Voorwaarden"), href: "/terms" },
                { label: "Privacy", href: "/privacy" },
                { label: "Cookies", href: "/cookies" },
              ].map((item) => (
                <li key={item.label}>
                  {item.href.startsWith("/") ? (
                    <Link to={item.href} className="font-label text-primary-foreground/30 hover:text-primary-foreground/70 transition-colors duration-700 text-xs tracking-wider uppercase">
                      {item.label}
                    </Link>
                  ) : (
                    <a href={item.href} className="font-label text-primary-foreground/30 hover:text-primary-foreground/70 transition-colors duration-700 text-xs tracking-wider uppercase">
                      {item.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>

            {/* Social Icons */}
            <div className="flex gap-3">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Contact Arzani Group on WhatsApp"
                className="w-9 h-9 border border-primary-foreground/20 rounded-md flex items-center justify-center text-primary-foreground/40 hover:text-primary-foreground/70 hover:border-primary-foreground/40 transition-all duration-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21"/><path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/></svg>
              </a>
              <a
                href="https://www.instagram.com/arzanigroup?igsh=Z2Rmb3dmajRjaDVz"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Arzani Group on Instagram"
                className="w-9 h-9 border border-primary-foreground/20 rounded-md flex items-center justify-center text-primary-foreground/40 hover:text-primary-foreground/70 hover:border-primary-foreground/40 transition-all duration-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                aria-label="Email Arzani Group"
                className="w-9 h-9 border border-primary-foreground/20 rounded-md flex items-center justify-center text-primary-foreground/40 hover:text-primary-foreground/70 hover:border-primary-foreground/40 transition-all duration-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              </a>
            </div>
          </div>
        </div>

        <div className="pt-10 border-t border-primary-foreground/5">
          <p className="font-label text-primary-foreground/15 text-center text-[9px]">
            © {new Date().getFullYear()} Arzani Group Worldwide. {t("All rights reserved.", "Alle rechten voorbehouden.")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
